import { useState, useEffect } from 'react';
import type { AdminPhoto, PhotoCategory } from '@/mocks/adminPhotos';
import { categoryLabels } from '@/mocks/adminPhotos';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface PhotoEditModalProps {
  open: boolean;
  photo: AdminPhoto | null;
  categories: PhotoCategory[];
  albums: { id: number; name: string }[];
  onClose: () => void;
  onSave: (data: { id: number; title: string; category: PhotoCategory; albumId: number }) => void;
}

export default function PhotoEditModal({ open, photo, categories, albums, onClose, onSave }: PhotoEditModalProps) {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<PhotoCategory>('eventos');
  const [albumId, setAlbumId] = useState<number>(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && photo) {
      setTitle(photo.title);
      setCategory(photo.category);
      setAlbumId(photo.albumId);
      setErrors({});
    }
  }, [open, photo]);

  if (!open || !photo) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = 'El título es obligatorio';
    if (title.length > 100) newErrors.title = 'Máximo 100 caracteres';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    onSave({
      id: photo.id,
      title: title.trim(),
      category,
      albumId,
    });
    setSaving(false);
    await SwalAlert.toast('Foto actualizada.', 'success');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative bg-background-50 rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div>
            <h2 className="text-base font-bold text-foreground-950">Editar foto</h2>
            <p className="text-[11px] text-foreground-400 mt-0.5">{photo.title}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-colors"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Preview */}
        <div className="aspect-video w-full overflow-hidden bg-background-200">
          <img
            src={photo.imageUrl}
            alt={photo.title}
            className="w-full h-full object-cover object-top"
          />
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-5 space-y-4">
            {/* Título */}
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Título <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={100}
                placeholder="Ej: Carlos al aire - Tardes de Pop"
                className={`w-full px-3.5 py-2.5 rounded-xl border bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all ${
                  errors.title ? 'border-red-400' : 'border-background-200'
                }`}
              />
              <div className="flex items-center justify-between mt-1">
                {errors.title && <p className="text-red-500 text-[10px]">{errors.title}</p>}
                <span className="text-[9px] text-foreground-400 ml-auto">{title.length}/100</span>
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Categoría</label>
              <div className="grid grid-cols-3 gap-1.5">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all border ${
                      category === cat
                        ? 'bg-primary-50 text-primary-700 border-primary-300'
                        : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                    }`}
                  >
                    {categoryLabels[cat]}
                  </button>
                ))}
              </div>
            </div>

            {/* Álbum */}
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">Álbum</label>
              <select
                value={albumId}
                onChange={(e) => setAlbumId(Number(e.target.value))}
                className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all cursor-pointer"
              >
                {albums.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Info */}
            <div className="bg-background-100 rounded-xl p-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-foreground-400">Tamaño</span>
                <span className="text-foreground-700 font-semibold">{photo.fileSize}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-foreground-400">Dimensiones</span>
                <span className="text-foreground-700 font-semibold">{photo.dimensions}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-foreground-400">Subida</span>
                <span className="text-foreground-700 font-semibold">
                  {new Date(photo.uploadedAt).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 px-5 py-4 border-t border-background-200 bg-background-100/70">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Guardando...
                </span>
              ) : (
                'Guardar cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}